import { ALLOWED_TLDS } from "../constants";
import extractDomain from "extract-domain";

/**
 * Normalize URL by adding https:// if no protocol is present
 */
export function normalizeUrl(url: string): string {
  // If it already has a protocol, return as-is
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  // Prepend https:// if no protocol is present
  return `https://${url}`;
}

/**
 * Extract TLD from URL - matches Remirror's approach
 * Uses extract-domain package like Remirror does, then splits by "." and takes last part
 */
export function extractTLD(url: string): string | null {
  const domain = extractDomain(url);
  if (!domain || domain === "") {
    return null;
  }
  const parts = domain.split(".");
  if (parts.length >= 1) {
    return parts[parts.length - 1].toLowerCase();
  }
  return null;
}

/**
 * Check if a URL points to an image file based on its extension
 * Validates both that it has an image extension AND looks like a URL (not just a filename)
 *
 * @param url - The URL to check
 * @returns true if URL has an image file extension and URL-like structure, false otherwise
 */
export function isImageUrl(url: string | undefined): boolean {
  if (!url) return false;

  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".svg",
    ".avif",
  ];

  const lowerUrl = url.toLowerCase();

  // Check if it has image extension
  const hasImageExtension = imageExtensions.some((ext) =>
    lowerUrl.includes(ext),
  );
  if (!hasImageExtension) return false;

  // Check if it looks like a URL (has / for paths, or domain structure with multiple dots)
  const looksLikeUrl =
    lowerUrl.includes("/") || (lowerUrl.match(/\./g) || []).length >= 2;
  return looksLikeUrl;
}

/**
 * Check if a URL points to a video file based on its extension
 * Validates both that it has a video extension AND looks like a URL (not just a filename)
 *
 * @param url - The URL to check
 * @returns true if URL has a video file extension and URL-like structure, false otherwise
 */
export function isVideoUrl(url: string | undefined): boolean {
  if (!url) return false;

  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];

  const lowerUrl = url.toLowerCase();

  // Check if it has video extension
  const hasVideoExtension = videoExtensions.some((ext) =>
    lowerUrl.includes(ext),
  );
  if (!hasVideoExtension) return false;

  // Check if it looks like a URL (has / for paths, or domain structure with multiple dots)
  const looksLikeUrl =
    lowerUrl.includes("/") || (lowerUrl.match(/\./g) || []).length >= 2;
  return looksLikeUrl;
}

/**
 * Check if a URL points to an image or video file
 *
 * @param url - The URL to check
 * @returns true if URL has an image or video file extension, false otherwise
 */
export function isMediaUrl(url: string | undefined): boolean {
  return isImageUrl(url) || isVideoUrl(url);
}

/**
 * Validate if a URL is valid
 *
 * Note: This function expects a normalized URL (with protocol).
 * Use normalizeUrl() first if needed.
 *
 * Checks:
 * 1. Uses URL constructor to validate proper URL structure (rejects HTML, invalid characters)
 * 2. If allowMedia is true, accepts image and video URLs without TLD validation
 * 3. Otherwise, validates TLD is in the allowed list (same as Remirror's autoLinkAllowedTLDs)
 *
 * @param url - The normalized URL to validate (can be undefined or empty)
 * @param options - Optional configuration object
 * @param options.allowMedia - If true, accepts image and video URLs without TLD validation
 * @returns true if URL is valid, empty, or undefined; false otherwise
 */
export function isValidUrl(
  url: string | undefined,
  options?: { allowMedia?: boolean },
): boolean {
  if (!url) return true; // Allow empty/undefined since user can delete the link

  const trimmed = url.trim();
  if (!trimmed) return true; // Allow empty after trimming

  // Use URL constructor to validate proper URL structure
  // This rejects HTML, invalid characters, and malformed URLs
  try {
    new URL(trimmed);
  } catch {
    return false;
  }

  // If allowMedia is enabled, check if it's a media URL (image or video)
  if (options?.allowMedia && isMediaUrl(trimmed)) {
    return true;
  }

  // Validate TLD matches allowed list (same as Remirror's autoLinkAllowedTLDs)
  const tld = extractTLD(trimmed);
  if (!tld) {
    return false;
  }

  return ALLOWED_TLDS.includes(tld);
}
